import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import getConfig from 'next/config';
import { useAuthentication } from '@context/businessContainer';
import { CREATE_COBBAI_CHAT_TOKEN } from '@monofront/business/queries';
import logger from '@utils/logger';
import { useMediaQuery } from '@monofront/business/hooks';
import { useGlobalSearchContext } from '@context/GlobalSearchContext';

const {
  publicRuntimeConfig: { cobbaiChatIdConnected, cobbaiChatIdAnonymous },
} = getConfig();

const COBBAI_INIT_SCRIPT_ID = 'cobbai-chat-init-script';

const COBBAI_INIT_SCRIPT_URL =
  'https://lafourche.cobbai.com/connectors/chats/conversations/init';

interface CreateCobbaiChatTokenResponse {
  createCobbaiChatToken: string;
}

const teardownCobbai = () => {
  try {
    document.getElementById(COBBAI_INIT_SCRIPT_ID)?.remove();

    // Best-effort cleanup of any DOM Cobbai injected. Exact selectors
    // will be confirmed with Cobbai support and refined in a follow-up.
    document.querySelectorAll('[id^="cobbai-"]').forEach((node) => {
      if (node.id !== COBBAI_INIT_SCRIPT_ID) {
        node.remove();
      }
    });

    window.CobbaiChatSessionEstablished = undefined;

    Object.keys(window.localStorage)
      .filter((key) => key.toLowerCase().startsWith('cobbai'))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    logger.error('Cobbai teardown failed', { error });
  }
};

const injectCobbaiScript = (chatId: string, authToken?: string) => {
  const script = document.createElement('script');
  const params: Record<string, string> = { chat_id: chatId };

  if (authToken) {
    params.auth_token = authToken;
  }

  script.id = COBBAI_INIT_SCRIPT_ID;
  script.async = true;
  script.src = `${COBBAI_INIT_SCRIPT_URL}?${new URLSearchParams(
    params,
  ).toString()}`;
  document.head.appendChild(script);
};

const CobbaiWidget = () => {
  const { customer } = useAuthentication();
  const [createCobbaiChatToken] = useMutation<CreateCobbaiChatTokenResponse>(
    CREATE_COBBAI_CHAT_TOKEN,
  );
  const isMobileWebsite = useMediaQuery(800);
  const { isGlobalSearchResultsPanelOpen } = useGlobalSearchContext();
  const isMobileSearchPanelOpen =
    isMobileWebsite && isGlobalSearchResultsPanelOpen;

  useEffect(() => {
    const uid = customer?.uid;

    if (uid) {
      if (!cobbaiChatIdConnected) {
        return undefined;
      }

      let cancelled = false;

      createCobbaiChatToken()
        .then(({ data }) => {
          if (cancelled || !data?.createCobbaiChatToken) {
            return;
          }

          injectCobbaiScript(cobbaiChatIdConnected, data.createCobbaiChatToken);
        })
        .catch((error) => {
          logger.error('Failed to mint Cobbai chat token', { error });
        });

      return () => {
        cancelled = true;
        teardownCobbai();
      };
    }

    if (!cobbaiChatIdAnonymous) {
      return undefined;
    }

    injectCobbaiScript(cobbaiChatIdAnonymous);

    return () => {
      teardownCobbai();
    };
  }, [customer?.uid, createCobbaiChatToken]);

  return (
    <style jsx global>{`
      :global([id^='cobbai-']) {
        ${isMobileSearchPanelOpen ? 'display: none !important;' : ''}
      }

      @media (max-width: 600px) {
        :global([id^='cobbai-']) {
          margin-bottom: 10px;
        }
      }

      @media (min-width: 600px) and (max-width: 800px) {
        :global([id^='cobbai-']) {
          margin-bottom: 50px;
        }
      }
    `}</style>
  );
};

export default CobbaiWidget;
